import { gql } from "graphql-request";
import type { GraphQLClient } from "graphql-request";
import { ScopeRequiredError } from "@nexus/widget-sdk";
import type { JsonValue, WidgetDataRequirements } from "@nexus/widget-sdk";
import type {
  BlockingInterval,
  BlockingIntervalStatus,
  CategorySeriesData,
  IpBlockingSeriesData,
  NetworkIdWeeklyData,
  SupplyChainData,
  WeightedTermsData,
  PiracyAudienceSeriesData,
  ReportFreshness,
  ReportMetric,
  ReportMetricFormat,
  ReportMetricsData,
  RankedSharesData,
} from "@nexus/widgets";
import type {
  DataResolver,
  SourceMetricDefinition,
  SourceSelection,
} from "@/lib/dashboard/resolverTypes";

/**
 * Static report sources.
 *
 * Each request has an explicit tenant binding (`params.tenant_ids`). The
 * dashboard engine can narrow that binding with viewer tenant selection;
 * report periods remain report-owned. The server authorizes requested tenants.
 */

// ── Shared report types and normalization ─────────────────────────────────────

interface GqlReportMetric {
  id: string;
  label: string;
  value: number | null;
  format: string;
  unit: string | null;
  comparison_value?: number | null;
  delta_percent?: number | null;
  occurred_at?: string | null;
  coverage?: number | null;
  period: { key: string; from_ts: string; to_ts: string; label: string };
  /** Report snapshots only: the metric's history, oldest first. */
  series?: number[] | null;
}

interface GqlReportFreshness {
  generated_at: string;
  data_through: string;
  source: string;
  is_estimated: boolean;
}

const METRIC_FORMATS: readonly ReportMetricFormat[] = ["number", "percent", "decimal"];

export function normalizeReportMetric(metric: GqlReportMetric): ReportMetric {
  return {
    id: metric.id,
    label: metric.label,
    value: metric.value,
    format: METRIC_FORMATS.includes(metric.format as ReportMetricFormat)
      ? (metric.format as ReportMetricFormat)
      : "number",
    unit: metric.unit,
    period: {
      key: metric.period.key,
      from: metric.period.from_ts,
      to: metric.period.to_ts,
      label: metric.period.label,
    },
    ...(metric.comparison_value === undefined ? {} : { comparisonValue: metric.comparison_value }),
    ...(metric.delta_percent === undefined ? {} : { deltaPercent: metric.delta_percent }),
    ...(metric.occurred_at ? { occurredAt: metric.occurred_at } : {}),
    ...(typeof metric.coverage === "number" ? { coverage: metric.coverage } : {}),
    ...(Array.isArray(metric.series) ? { series: metric.series.filter(Number.isFinite) } : {}),
  };
}

export function normalizeReportFreshness(freshness: GqlReportFreshness): ReportFreshness {
  return {
    generatedAt: freshness.generated_at,
    dataThrough: freshness.data_through,
    source: freshness.source,
    isEstimated: freshness.is_estimated,
  };
}

/** The period a series was requested for, so its chart can span it whole. */
export function periodOf(params: Record<string, unknown>): {
  period?: { from: string; to: string };
} {
  return typeof params.from_ts === "string" && typeof params.to_ts === "string"
    ? { period: { from: params.from_ts, to: params.to_ts } }
    : {};
}

export function normalizeReportMetrics(report: {
  metrics?: GqlReportMetric[];
  freshness?: GqlReportFreshness | null;
  subject?: { title: string; subtitle?: string | null; image?: string | null } | null;
}): ReportMetricsData {
  return {
    metrics: (report.metrics ?? []).map(normalizeReportMetric),
    freshness: report.freshness ? normalizeReportFreshness(report.freshness) : null,
    ...(report.subject?.title
      ? {
          subject: {
            title: report.subject.title,
            subtitle: report.subject.subtitle ?? null,
            image: report.subject.image ?? null,
          },
        }
      : {}),
  };
}

/**
 * Set by the dashboard data layer when the viewer's tenant selection and the
 * tenants a report covers have nothing in common, so the widget can say why it
 * is empty instead of claiming that no tenant was ever chosen.
 */
export const OUTSIDE_TENANT_SCOPE = "outside_tenant_scope";

export const NO_REPORT_TENANTS_MESSAGE = "No tenants chosen for this report yet.";
export const OUTSIDE_TENANTS_MESSAGE = "The selected tenants are not covered by this report.";

/**
 * The tenant scope a report dashboard owns. A report never widens to "all
 * tenants": a missing or malformed scope is an error the widget surfaces, in
 * words a viewer can act on.
 */
export function requireTenantIds(value: JsonValue | undefined): number[] {
  const valid =
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((entry) => typeof entry === "number" && Number.isInteger(entry) && entry > 0);
  if (!valid) throw new ScopeRequiredError(NO_REPORT_TENANTS_MESSAGE);
  return value as number[];
}

// ── Piracy audience ───────────────────────────────────────────────────────────

const HERMES_SOURCES_QUERY = gql`
  query PiracyAudienceHermesSources($tenant_ids: [Int!]!) {
    piracy_audience_hermes_sources(tenant_ids: $tenant_ids)
  }
`;

export async function fetchAudienceHermesSources(
  tenantIds: readonly number[],
  client: GraphQLClient,
  signal?: AbortSignal,
): Promise<string[]> {
  const ids = [...new Set(requireTenantIds([...tenantIds]))].sort((left, right) => left - right);
  const raw = await client.request<{ piracy_audience_hermes_sources: string[] }>({
    document: HERMES_SOURCES_QUERY,
    variables: { tenant_ids: ids },
    signal,
  });
  return [...new Set(raw.piracy_audience_hermes_sources)].sort();
}

export function usesAudienceHermesSources(
  requirement: WidgetDataRequirements | undefined,
): boolean {
  if (!requirement || requirement.mode === "complete") return true;
  return Boolean(
    requirement.defaultMetric ||
    requirement.metricIds?.includes("viewers-disrupted") ||
    (requirement.fields?.includes("metrics") && requirement.metricIds === undefined) ||
    requirement.fields?.some(
      (field) =>
        field === "blockingIntervals" ||
        field === "blockingPeriods" ||
        field.startsWith("blockingPeriods."),
    ),
  );
}

export function usesAudiencePlatforms(requirement: WidgetDataRequirements | undefined): boolean {
  if (!requirement || requirement.mode === "complete") return true;
  return Boolean(
    requirement.fields?.some((field) => field === "points" || field.startsWith("points.")) ||
    (requirement.fields?.includes("metrics") &&
      !requirement.defaultMetric &&
      (requirement.metricIds === undefined ||
        requirement.metricIds.some((id) => id === "average-viewers" || id === "max-viewers"))),
  );
}

export type PlatformCatalogue = "audience" | "ip" | "domain" | "ip-hosts" | "domain-hosts";

export function platformCatalogueForSource(source: string): PlatformCatalogue | undefined {
  if (source === "piracy-audience" || source === "piracy-audience-metrics") return "audience";
  if (source === "ip-blocking-combined-hosts") return "ip-hosts";
  if (source === "domain-blocking-combined-hosts") return "domain-hosts";
  if (["ip-blocking-cohorts", "ip-blocking-effectiveness", "blocking-overview-ip"].includes(source))
    return "ip";
  if (
    [
      "domain-blocking-cohorts",
      "domain-blocking-effectiveness",
      "blocking-overview-domain",
    ].includes(source)
  )
    return "domain";
  return undefined;
}

export async function fetchReportPlatforms(
  catalogue: PlatformCatalogue,
  tenantIds: readonly number[],
  client: GraphQLClient,
  signal?: AbortSignal,
): Promise<string[]> {
  const ids = [...new Set(requireTenantIds([...tenantIds]))].sort((left, right) => left - right);
  const audience = catalogue === "audience";
  const raw = await client.request<{
    piracy_audience_platforms?: string[];
    ip_blocking_platforms?: string[];
  }>({
    document: audience
      ? gql`
          query AudiencePlatforms($tenant_ids: [Int!]!) {
            piracy_audience_platforms(tenant_ids: $tenant_ids)
          }
        `
      : gql`
          query BlockingPlatforms($tenant_ids: [Int!]!, $target_kind: String!, $hosts: Boolean!) {
            ip_blocking_platforms(tenant_ids: $tenant_ids, target_kind: $target_kind, hosts: $hosts)
          }
        `,
    variables: audience
      ? { tenant_ids: ids }
      : {
          tenant_ids: ids,
          target_kind: catalogue.startsWith("domain") ? "DOMAIN" : "IP",
          hosts: catalogue.endsWith("hosts"),
        },
    signal,
  });
  return [...new Set(audience ? raw.piracy_audience_platforms : raw.ip_blocking_platforms)].sort();
}

/** ISPs tickets were sent to over the last 90 days, offered as blocking exclusions. */
export async function fetchBlockingIsps(
  catalogue: "ip" | "domain",
  tenantIds: readonly number[],
  client: GraphQLClient,
  signal?: AbortSignal,
): Promise<string[]> {
  const ids = [...new Set(requireTenantIds([...tenantIds]))].sort((left, right) => left - right);
  const raw = await client.request<{ ip_blocking_isps: string[] }>({
    document: gql`
      query BlockingIsps($tenant_ids: [Int!]!, $target_kind: String!) {
        ip_blocking_isps(tenant_ids: $tenant_ids, target_kind: $target_kind)
      }
    `,
    variables: { tenant_ids: ids, target_kind: catalogue === "domain" ? "DOMAIN" : "IP" },
    signal,
  });
  return [...new Set(raw.ip_blocking_isps)].sort();
}

const PIRACY_AUDIENCE_METRICS_QUERY = gql`
  query PiracyAudienceMetrics(
    $tenant_ids: [Int!]!
    $countries: [String!]
    $hermes_sources: [String!]
    $platforms: [String!]
    $from_ts: String
    $to_ts: String
    $metric_ids: [String!]
    $include_comparison: Boolean! = true
    $include_metrics: Boolean! = true
    $include_freshness: Boolean! = true
  ) {
    piracy_audience_report(
      tenant_ids: $tenant_ids
      countries: $countries
      hermes_sources: $hermes_sources
      platforms: $platforms
      from_ts: $from_ts
      to_ts: $to_ts
      metric_ids: $metric_ids
    ) {
      metrics @include(if: $include_metrics) {
        id
        label
        value
        format
        unit
        comparison_value @include(if: $include_comparison)
        delta_percent @include(if: $include_comparison)
        occurred_at
        coverage
        period {
          key
          from_ts
          to_ts
          label
        }
      }
      freshness @include(if: $include_freshness) {
        generated_at
        data_through
        source
        is_estimated
      }
    }
  }
`;

type PiracyAudienceMetricsResponse = {
  piracy_audience_report: { metrics: GqlReportMetric[]; freshness: GqlReportFreshness };
};

const PIRACY_AUDIENCE_SERIES_QUERY = gql`
  query PiracyAudienceSeries(
    $tenant_ids: [Int!]!
    $countries: [String!]
    $hermes_sources: [String!]
    $platforms: [String!]
    $from_ts: String
    $to_ts: String
    $include_points: Boolean! = true
    $include_intervals: Boolean! = true
    $include_periods: Boolean! = true
    $include_impact: Boolean! = true
    $include_blocking_applies: Boolean! = true
    $include_freshness: Boolean! = true
  ) {
    piracy_audience_report(
      tenant_ids: $tenant_ids
      countries: $countries
      hermes_sources: $hermes_sources
      platforms: $platforms
      from_ts: $from_ts
      to_ts: $to_ts
    ) {
      series @include(if: $include_points) {
        bucket
        safe_to_block_audience
        unsafe_to_block_audience
        observed_audience
        estimated_audience
      }
      blocking_intervals @include(if: $include_intervals) {
        from_ts
        to_ts
        status
        provider
      }
      blocking_periods @include(if: $include_periods) {
        id
        from_ts
        to_ts
        status
        viewers_disrupted @include(if: $include_impact)
      }
      blocking_applies @include(if: $include_blocking_applies)
      freshness @include(if: $include_freshness) {
        generated_at
        data_through
        source
        is_estimated
      }
    }
  }
`;

export interface GqlPiracyAudienceSeries {
  series?: {
    bucket: string;
    safe_to_block_audience: number;
    unsafe_to_block_audience: number;
    observed_audience: number | null;
    estimated_audience?: number | null;
  }[];
  blocking_intervals?: {
    from_ts: string;
    to_ts: string;
    status: string;
    provider: string | null;
  }[];
  blocking_periods?:
    | {
        id: string;
        from_ts: string;
        to_ts: string;
        status: string;
        viewers_disrupted?: number | null;
      }[]
    | null;
  blocking_applies?: boolean | null;
  freshness?: GqlReportFreshness | null;
}

const BLOCKING_STATUSES: readonly BlockingIntervalStatus[] = ["active", "completed"];

export function normalizePiracyAudienceSeries(
  report: GqlPiracyAudienceSeries,
): PiracyAudienceSeriesData {
  return {
    points: (report.series ?? []).map((point) => ({
      timestamp: point.bucket,
      safeToBlock: point.safe_to_block_audience,
      unsafeToBlock: point.unsafe_to_block_audience,
      observed: point.observed_audience,
      ...(point.estimated_audience != null ? { estimated: point.estimated_audience } : {}),
    })),
    blockingIntervals: (report.blocking_intervals ?? []).map((interval): BlockingInterval => ({
      from: interval.from_ts,
      to: interval.to_ts,
      // An unknown status is treated as finished rather than claiming live blocking.
      status: BLOCKING_STATUSES.includes(interval.status as BlockingIntervalStatus)
        ? (interval.status as BlockingIntervalStatus)
        : "completed",
      provider: interval.provider,
    })),
    freshness: report.freshness ? normalizeReportFreshness(report.freshness) : null,
    ...(report.blocking_applies != null ? { blockingApplies: report.blocking_applies } : {}),
    ...(report.blocking_periods != null
      ? {
          blockingPeriods: report.blocking_periods.map((item) => ({
            id: item.id,
            from: item.from_ts,
            to: item.to_ts,
            status: item.status,
            ...(item.viewers_disrupted === undefined
              ? {}
              : { viewersDisrupted: item.viewers_disrupted }),
          })),
        }
      : {}),
  };
}

// ── IP blocking ───────────────────────────────────────────────────────────────

const IP_BLOCKING_METRICS_QUERY = gql`
  query IpBlockingMetrics(
    $tenant_ids: [Int!]!
    $from_ts: String
    $to_ts: String
    $platform: String
    $platforms: [String!]
    $metric_ids: [String!]
    $include_comparison: Boolean! = true
    $include_metrics: Boolean! = true
    $include_freshness: Boolean! = true
  ) {
    ip_blocking_report(
      tenant_ids: $tenant_ids
      from_ts: $from_ts
      to_ts: $to_ts
      platform: $platform
      platforms: $platforms
      metric_ids: $metric_ids
      include_comparison: $include_comparison
    ) {
      metrics @include(if: $include_metrics) {
        id
        label
        value
        format
        unit
        comparison_value @include(if: $include_comparison)
        delta_percent @include(if: $include_comparison)
        period {
          key
          from_ts
          to_ts
          label
        }
      }
      freshness @include(if: $include_freshness) {
        generated_at
        data_through
        source
        is_estimated
      }
    }
  }
`;

const IP_BLOCKING_SERIES_QUERY = gql`
  query IpBlockingSeries(
    $tenant_ids: [Int!]!
    $from_ts: String
    $to_ts: String
    $platform: String
    $platforms: [String!]
  ) {
    ip_blocking_report(
      tenant_ids: $tenant_ids
      from_ts: $from_ts
      to_ts: $to_ts
      platform: $platform
      platforms: $platforms
    ) {
      series {
        bucket
        detected_unique_ips
        sent_unique_ips
      }
      cohorts {
        daily {
          bucket
          safe_unique_ips
        }
      }
      coverage {
        correlated
        detection_tenant_ids
        sent_tenant_ids
      }
      freshness {
        generated_at
        data_through
        source
        is_estimated
      }
    }
  }
`;

const IP_BLOCKING_TOP_HOSTS_QUERY = gql`
  query IpBlockingTopHosts(
    $tenant_ids: [Int!]!
    $from_ts: String
    $to_ts: String
    $platform: String
    $platforms: [String!]
  ) {
    ip_blocking_report(
      tenant_ids: $tenant_ids
      from_ts: $from_ts
      to_ts: $to_ts
      platform: $platform
      platforms: $platforms
    ) {
      top_hosts {
        channel
        label
        unique_ips
        hosts {
          name
          share
        }
      }
    }
  }
`;

interface GqlIpBlockingTopHosts {
  top_hosts: {
    channel: string;
    label: string;
    unique_ips: number;
    attributed_unique_ips?: number | null;
    unattributed_unique_ips?: number | null;
    hosts: { name: string; share: number }[];
  }[];
}

/**
 * The report's hosts per channel as `ranked-shares` lists. The report shape is
 * provisional: when it changes, only this mapping (and the query) follows.
 */
export function normalizeIpBlockingTopHosts(
  report: GqlIpBlockingTopHosts,
  targetKind: "IP" | "DOMAIN" = "IP",
): RankedSharesData {
  return {
    lists: (report.top_hosts ?? []).map((channel) => ({
      id: channel.channel,
      label: channel.label,
      total: { value: channel.unique_ips, unit: targetKind === "DOMAIN" ? "Domain" : "IP" },
      ...(channel.attributed_unique_ips != null && channel.unattributed_unique_ips != null
        ? {
            coverage: {
              attributed: channel.attributed_unique_ips,
              unattributed: channel.unattributed_unique_ips,
            },
          }
        : {}),
      items: [...channel.hosts]
        .filter((host) => Number.isFinite(host.share) && host.share > 0)
        .sort((a, b) => b.share - a.share)
        .map((host) => ({ label: host.name, share: host.share })),
    })),
  };
}

interface GqlIpBlockingSeries {
  cohorts?: { daily: { bucket: string; safe_unique_ips: number | null }[] } | null;
  series: {
    bucket: string;
    detected_unique_ips: number | null;
    sent_unique_ips: number | null;
    confirmed_blocked_unique_ips?: number | null;
  }[];
  freshness: GqlReportFreshness;
  coverage?: {
    correlated: boolean;
    detection_tenant_ids: number[];
    sent_tenant_ids: number[];
    confirmation_tenant_ids?: number[];
  };
}

export function normalizeIpBlockingSeries(report: GqlIpBlockingSeries): IpBlockingSeriesData {
  return {
    ...(report.coverage
      ? {
          coverage: {
            correlated: report.coverage.correlated,
            detectionTenantIds: report.coverage.detection_tenant_ids,
            sentTenantIds: report.coverage.sent_tenant_ids,
            confirmationTenantIds: report.coverage.confirmation_tenant_ids ?? [],
          },
        }
      : {}),
    points: report.series.map((point) => ({
      day: point.bucket,
      detected: point.detected_unique_ips,
      sent: point.sent_unique_ips,
      confirmedBlocked: point.confirmed_blocked_unique_ips ?? null,
      safeToBlock:
        report.cohorts?.daily.find((item) => item.bucket === point.bucket)?.safe_unique_ips ?? null,
    })),
    freshness: normalizeReportFreshness(report.freshness),
  };
}

// ── Network ID ────────────────────────────────────────────────────────────────

const NETWORK_ID_QUERY = gql`
  query NetworkIdReport($tenant_ids: [Int!]!, $from_ts: String, $to_ts: String) {
    network_id_report(tenant_ids: $tenant_ids, from_ts: $from_ts, to_ts: $to_ts) {
      series {
        tenant_id
        bucket
        network_type
        count
      }
      freshness {
        generated_at
        data_through
        source
        is_estimated
      }
    }
  }
`;

interface GqlNetworkIdReport {
  series: { tenant_id: number; bucket: string; network_type: string; count: number }[];
  freshness: GqlReportFreshness;
}

export function normalizeNetworkIdReport(report: GqlNetworkIdReport): CategorySeriesData {
  return {
    points: report.series.map((point) => ({
      tenantId: point.tenant_id,
      bucket: point.bucket,
      category: point.network_type,
      count: point.count,
    })),
    freshness: normalizeReportFreshness(report.freshness),
  };
}

const NETWORK_ID_WEEKLY_QUERY = gql`
  query NetworkIdWeekly(
    $tenant_ids: [Int!]!
    $from_ts: String!
    $to_ts: String!
    $stream_ids: [Int!]
  ) {
    network_id_weekly(
      tenant_ids: $tenant_ids
      from_ts: $from_ts
      to_ts: $to_ts
      stream_ids: $stream_ids
    ) {
      tenant_id
      weeks {
        week_start
        total
        partial
        groups {
          network_group
          network_sub_level
          count
        }
      }
      freshness {
        generated_at
        data_through
        source
        is_estimated
      }
    }
  }
`;

interface GqlNetworkIdWeekly {
  tenant_id: number;
  weeks: {
    week_start: string;
    total: number;
    partial: boolean;
    groups: { network_group: string; network_sub_level: string | null; count: number }[];
  }[];
  freshness: GqlReportFreshness;
}

export function normalizeNetworkIdWeekly(report: GqlNetworkIdWeekly): NetworkIdWeeklyData {
  return {
    tenantId: report.tenant_id,
    weeks: report.weeks.map((week) => ({
      weekStart: week.week_start,
      total: week.total,
      partial: week.partial,
      groups: week.groups.map((group) => ({
        networkGroup: group.network_group,
        networkSubLevel: group.network_sub_level ?? null,
        count: group.count,
      })),
    })),
    freshness: normalizeReportFreshness(report.freshness),
  };
}

/** One tenant, optional streams and the dashboard period, which this source cannot do without. */
function networkIdWeeklyParams(params: Record<string, JsonValue>): Record<string, unknown> {
  const base = reportParams(params);
  if (!("from_ts" in base)) throw new ScopeRequiredError("Choose a dashboard time range.");
  const streams = params.stream_ids;
  if (streams === undefined || streams === null) return base;
  if (
    !Array.isArray(streams) ||
    !streams.every((entry) => typeof entry === "number" && Number.isInteger(entry) && entry > 0)
  )
    throw new Error("stream_ids must be a list of stream IDs, for example [12, 15].");
  return streams.length > 0
    ? { ...base, stream_ids: [...new Set(streams as number[])].sort((a, b) => a - b) }
    : base;
}

// ── OTT piracy ────────────────────────────────────────────────────────────────

const OTT_PIRACY_METRICS_QUERY = gql`
  query OttPiracyMetrics($tenant_ids: [Int!]!, $from_ts: String, $to_ts: String) {
    ott_piracy_report(tenant_ids: $tenant_ids, from_ts: $from_ts, to_ts: $to_ts) {
      metrics {
        id
        label
        value
        format
        unit
        comparison_value
        delta_percent
        period {
          key
          from_ts
          to_ts
          label
        }
      }
      freshness {
        generated_at
        data_through
        source
        is_estimated
      }
    }
  }
`;

const OTT_PIRACY_TERMS_QUERY = gql`
  query OttPiracyTerms($tenant_ids: [Int!]!, $from_ts: String, $to_ts: String) {
    ott_piracy_report(tenant_ids: $tenant_ids, from_ts: $from_ts, to_ts: $to_ts) {
      top_services {
        id
        label
        weight
      }
      freshness {
        generated_at
        data_through
        source
        is_estimated
      }
    }
  }
`;

const OTT_PIRACY_CHAIN_QUERY = gql`
  query OttPiracyChain($tenant_ids: [Int!]!, $from_ts: String, $to_ts: String) {
    ott_piracy_report(tenant_ids: $tenant_ids, from_ts: $from_ts, to_ts: $to_ts) {
      nodes {
        id
        label
        stage
        value
        members
      }
      links {
        source_id
        target_id
        value
      }
      freshness {
        generated_at
        data_through
        source
        is_estimated
      }
    }
  }
`;

export function normalizeOttTerms(report: {
  top_services: { id: string; label: string; weight: number }[];
  freshness: GqlReportFreshness;
}): WeightedTermsData {
  return {
    terms: report.top_services.map((term) => ({
      id: term.id,
      label: term.label,
      weight: term.weight,
    })),
    freshness: normalizeReportFreshness(report.freshness),
  };
}

export function normalizeOttChain(report: {
  nodes: { id: string; label: string; stage: string; value: number; members?: number | null }[];
  links: { source_id: string; target_id: string; value: number }[];
  freshness: GqlReportFreshness;
}): SupplyChainData {
  return {
    nodes: report.nodes.map((node) => ({
      id: node.id,
      label: node.label,
      stage: node.stage,
      value: node.value,
      ...(typeof node.members === "number" ? { members: node.members } : {}),
    })),
    links: report.links.map((link) => ({
      source: link.source_id,
      target: link.target_id,
      value: link.value,
    })),
    freshness: normalizeReportFreshness(report.freshness),
  };
}

export function reportParams(params: Record<string, JsonValue>): Record<string, unknown> {
  if (params[OUTSIDE_TENANT_SCOPE] === true) throw new ScopeRequiredError(OUTSIDE_TENANTS_MESSAGE);
  return {
    tenant_ids: requireTenantIds(params.tenant_ids),
    ...Object.fromEntries(
      [
        "include_comparison",
        "include_metrics",
        "include_freshness",
        "include_points",
        "include_intervals",
        "include_periods",
        "include_impact",
        "include_status_hours",
      ].flatMap((key) => (typeof params[key] === "boolean" ? [[key, params[key]]] : [])),
    ),
    ...(typeof params.from_ts === "string" && typeof params.to_ts === "string"
      ? { from_ts: params.from_ts, to_ts: params.to_ts }
      : {}),
  };
}

const AUDIENCE_METRICS = [
  { id: "viewers-disrupted", label: "Viewers disrupted" },
  { id: "average-viewers", label: "Average viewers" },
  { id: "max-viewers", label: "Max viewers" },
] as const;
const IP_METRICS = [
  { id: "ip-blocking-unique-detected", label: "Unique IPs detected" },
  { id: "ip-blocking-unique", label: "Unique IPs sent for blocking" },
  { id: "ip-blocking-detected", label: "IPs detected" },
  { id: "ip-blocking-sent", label: "IPs sent for blocking" },
  ...["", "-iptv", "-web"].map((suffix) => ({
    id: `ip-blocking-sent-ratio${suffix}`,
    label: `Distinct sent / detected${suffix}`,
  })),
] as const;

function metricSelection(metrics: readonly SourceMetricDefinition[]): SourceSelection {
  return {
    mode: "select",
    fields: ["metrics", "freshness"],
    metrics,
    toParams: ({ metricIds, fields, includeComparison }) => ({
      ...(metricIds === undefined ? {} : { metric_ids: [...metricIds] }),
      include_comparison: includeComparison ?? false,
      include_metrics: fields?.includes("metrics") ?? true,
      include_freshness: fields?.includes("freshness") ?? true,
    }),
  };
}

function metricIdsParams(
  params: Record<string, JsonValue>,
  metrics: readonly SourceMetricDefinition[],
): Record<string, unknown> {
  const metricIds = params.metric_ids;
  if (metricIds === undefined) return {};
  if (
    !Array.isArray(metricIds) ||
    metricIds.some((id) => typeof id !== "string" || !metrics.some((metric) => metric.id === id))
  ) {
    throw new Error("Choose valid report metric IDs");
  }
  return { metric_ids: [...new Set(metricIds)].sort() };
}

function platformParams(params: Record<string, JsonValue>): { platforms?: string[] } {
  const values = params.platforms;
  if (
    values !== undefined &&
    (!Array.isArray(values) ||
      values.some(
        (value) => typeof value !== "string" || !value.trim() || value.trim().length > 128,
      ))
  )
    throw new Error("Choose valid platform identifiers");
  const platforms = Array.isArray(values)
    ? [...new Set((values as string[]).map((value) => value.trim().toLowerCase()))].sort()
    : [];
  return platforms.length ? { platforms } : {};
}

/** ISP keys left out of blocking effectiveness, matched exactly as Hermes sends them. */
function excludedIspsParams(params: Record<string, JsonValue>): { excluded_isps?: string[] } {
  const values = params.excluded_isps;
  if (
    values !== undefined &&
    (!Array.isArray(values) ||
      values.some((value) => typeof value !== "string" || !value || value.length > 256))
  )
    throw new Error("Choose valid ISP names to exclude");
  const isps = Array.isArray(values) ? [...new Set(values as string[])].sort() : [];
  return isps.length ? { excluded_isps: isps } : {};
}

function audienceParams(params: Record<string, JsonValue>): Record<string, unknown> {
  const countries = params.countries;
  if (
    countries !== undefined &&
    (!Array.isArray(countries) ||
      countries.some((code) => typeof code !== "string" || !/^[A-Za-z]{2}$/.test(code.trim())))
  )
    throw new Error("Choose valid two-letter country codes");
  const codes = Array.isArray(countries)
    ? [...new Set((countries as string[]).map((code) => code.trim().toUpperCase()))].sort()
    : [];
  const hermesSources = params.hermes_sources;
  if (
    hermesSources !== undefined &&
    (!Array.isArray(hermesSources) ||
      hermesSources.some(
        (source) => typeof source !== "string" || !/^[A-Za-z0-9_-]+$/.test(source.trim()),
      ))
  )
    throw new Error("Choose valid Hermes source identifiers");
  const sources = Array.isArray(hermesSources)
    ? [...new Set((hermesSources as string[]).map((source) => source.trim()))].sort()
    : [];
  return {
    ...reportParams(params),
    ...(codes.length ? { countries: codes } : {}),
    ...(sources.length ? { hermes_sources: sources } : {}),
    ...platformParams(params),
  };
}

function audienceMetricParams(params: Record<string, JsonValue>): Record<string, unknown> {
  return { ...audienceParams(params), ...metricIdsParams(params, AUDIENCE_METRICS) };
}

function ipBlockingParams(params: Record<string, JsonValue>): Record<string, unknown> {
  const platform = params.platform;
  if (platform !== undefined && platform !== "ALL" && platform !== "IPTV" && platform !== "WEB")
    throw new Error("Choose a valid IP Blocking platform: ALL, IPTV or WEB");
  return {
    ...reportParams(params),
    ...platformParams(params),
    ...(platform !== undefined ? { platform } : {}),
  };
}

interface GqlSendingCounts {
  eligible_sendings: number;
  blocked_sendings: number;
  ratio: number | null;
}

interface GqlIpBlockingCohorts {
  freshness: GqlReportFreshness;
  cohorts: {
    total: {
      detected_unique_ips: number | null;
      sent_unique_ips: number | null;
      approved_unique_ips?: number | null;
    };
    daily: {
      bucket: string;
      detected_unique_ips: number | null;
      sent_unique_ips: number | null;
      safe_unique_ips?: number | null;
      regulator_approved_unique_ips?: number | null;
    }[];
    observed_tenant_ids: number[];
  } | null;
  safety_evidence: {
    detected_unique_ips: number | null;
    safe_unique_ips: number | null;
    not_confirmed_safe_unique_ips: number | null;
    safe_ratio: number | null;
    safe_only_unique_ips: number | null;
    unsafe_only_unique_ips: number | null;
    conflicting_unique_ips: number | null;
    unknown_unique_ips: number | null;
    web_safe_ratio?: number | null;
    iptv_safe_ratio?: number | null;
    web_safe_unique_ips?: number | null;
    iptv_safe_unique_ips?: number | null;
  } | null;
  effectiveness?: {
    total: GqlSendingCounts;
    daily: (GqlSendingCounts & { bucket: string })[];
    isps: (GqlSendingCounts & { isp: string; country?: string | null })[];
    top_isps?: (GqlSendingCounts & { isp: string; country?: string | null })[];
    bottom_isps: (GqlSendingCounts & { isp: string; country?: string | null })[];
    eligible_targets?: number | null;
    blocked_targets?: number | null;
    majority_ratio?: number | null;
  } | null;
}

export function normalizeIpBlockingCohorts(
  report: GqlIpBlockingCohorts,
  targetKind: "IP" | "DOMAIN" = "IP",
): IpBlockingSeriesData {
  const cohorts = report.cohorts;
  const safety = report.safety_evidence;
  const effectiveness = report.effectiveness;
  const counts = (item: GqlSendingCounts) => ({
    eligibleSendings: item.eligible_sendings,
    blockedSendings: item.blocked_sendings,
    ratio: item.ratio,
  });
  const isp = (item: GqlSendingCounts & { isp: string; country?: string | null }) => ({
    ...counts(item),
    isp: item.isp,
    country: item.country ?? null,
  });
  return {
    targetKind,
    points: (cohorts?.daily ?? []).map((item) => ({
      day: item.bucket,
      detected: item.detected_unique_ips,
      sent: item.sent_unique_ips,
      confirmedBlocked: null,
      safeToBlock: item.safe_unique_ips ?? null,
      approved: item.regulator_approved_unique_ips ?? null,
    })),
    freshness: normalizeReportFreshness(report.freshness),
    ...(cohorts
      ? {
          cohorts: {
            total: {
              detected: cohorts.total.detected_unique_ips,
              sent: cohorts.total.sent_unique_ips,
              ...(cohorts.total.approved_unique_ips === undefined
                ? {}
                : { approved: cohorts.total.approved_unique_ips }),
            },
            observedTenantIds: cohorts.observed_tenant_ids,
          },
        }
      : {}),
    ...(safety
      ? {
          safety: {
            detected: safety.detected_unique_ips,
            safe: safety.safe_unique_ips,
            notConfirmedSafe: safety.not_confirmed_safe_unique_ips,
            ratio: safety.safe_ratio,
            safeOnly: safety.safe_only_unique_ips,
            unsafeOnly: safety.unsafe_only_unique_ips,
            conflicting: safety.conflicting_unique_ips,
            unknown: safety.unknown_unique_ips,
          },
        }
      : {}),
    ...(effectiveness
      ? {
          effectiveness: {
            total: counts(effectiveness.total),
            daily: effectiveness.daily.map((item) => ({ ...counts(item), day: item.bucket })),
            isps: effectiveness.isps.map(isp),
            topIsps: (effectiveness.top_isps ?? []).map(isp),
            bottomIsps: effectiveness.bottom_isps.map(isp),
            eligibleTargets: effectiveness.eligible_targets ?? null,
            blockedTargets: effectiveness.blocked_targets ?? null,
            majorityRatio: effectiveness.majority_ratio ?? null,
          },
        }
      : {}),
  };
}

function ipBlockingCohortSource(
  includeEffectiveness: boolean,
  targetKind: "IP" | "DOMAIN" = "IP",
): DataResolver {
  const sendingFields = "eligible_sendings blocked_sendings ratio";
  // Only the cohort trend follows the blocking period filter; effectiveness keeps creation days.
  const dateBasis = includeEffectiveness ? "" : ", $date_basis: String!";
  const query = gql`query ${includeEffectiveness ? "IpBlockingEffectiveness" : "IpBlockingCohorts"}($tenant_ids: [Int!]!, $from_ts: String, $to_ts: String, $platform: String, $platforms: [String!]${includeEffectiveness ? ", $excluded_isps: [String!]" : dateBasis}) {
    ip_blocking_report(tenant_ids: $tenant_ids, from_ts: $from_ts, to_ts: $to_ts, platform: $platform, platforms: $platforms, ${includeEffectiveness ? "excluded_isps: $excluded_isps, " : "date_basis: $date_basis, "}target_kind: ${JSON.stringify(targetKind)}) {
      cohorts { total { detected_unique_ips sent_unique_ips } daily { bucket detected_unique_ips sent_unique_ips safe_unique_ips${includeEffectiveness ? "" : " regulator_approved_unique_ips"} } observed_tenant_ids }
      safety_evidence { detected_unique_ips safe_unique_ips not_confirmed_safe_unique_ips safe_ratio safe_only_unique_ips unsafe_only_unique_ips conflicting_unique_ips unknown_unique_ips }
      freshness { generated_at data_through source is_estimated }
      ${includeEffectiveness ? `effectiveness { total { ${sendingFields} } daily { bucket ${sendingFields} } isps { isp country ${sendingFields} } bottom_isps { isp country ${sendingFields} } eligible_targets blocked_targets majority_ratio }` : ""}
    }
  }`;
  return {
    label: includeEffectiveness
      ? `${targetKind === "DOMAIN" ? "Domain" : "IP"} blocking effectiveness and cohorts`
      : `${targetKind === "DOMAIN" ? "Domain" : "IP"} Blocking Trend`,
    description:
      "Creation-cohort target volumes and disjoint safety evidence; effectiveness uses approved ticket/ISP sendings.",
    contract: { name: "ip-blocking-series", version: 1 },
    report: {
      paramKeys: includeEffectiveness
        ? ["tenant_ids", "platform", "platforms", "excluded_isps"]
        : ["tenant_ids", "platform", "platforms", "date_basis"],
      followScope: true,
    },
    buildParams: includeEffectiveness
      ? (params) => ({ ...ipBlockingParams(params), ...excludedIspsParams(params) })
      : (params) => ({ ...ipBlockingParams(params), date_basis: blockingDateBasis(params) }),
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<{ ip_blocking_report: GqlIpBlockingCohorts }>(query, params);
      const data = {
        ...normalizeIpBlockingCohorts(raw.ip_blocking_report, targetKind),
        ...periodOf(params),
      };
      if (includeEffectiveness) return data;
      return {
        ...data,
        dateBasis: params.date_basis === "blocking_window" ? "blocking_window" : "creation",
      } satisfies IpBlockingSeriesData;
    },
  };
}

function blockingOverviewSource(targetKind: "IP" | "DOMAIN"): DataResolver {
  const sendingFields = "eligible_sendings blocked_sendings ratio";
  const query = gql`query BlockingOverview($tenant_ids: [Int!]!, $from_ts: String, $to_ts: String, $platforms: [String!], $excluded_isps: [String!], $date_basis: String!, $country_source: String!, $include_status_hours: Boolean! = true) {
    ip_blocking_report(tenant_ids: $tenant_ids, from_ts: $from_ts, to_ts: $to_ts, platform: "ALL", platforms: $platforms, excluded_isps: $excluded_isps, date_basis: $date_basis, country_source: $country_source, target_kind: ${JSON.stringify(targetKind)}) {
      effectiveness {
        total { ${sendingFields} }
        isps { isp country ${sendingFields} }
      }
      status_hours @include(if: $include_status_hours) { status hours }
      freshness { generated_at data_through source is_estimated }
    }
  }`;
  return {
    label: targetKind === "IP" ? "IP Blocking Overview" : "Domain Blocking Overview",
    description:
      "Confirmed-blocking ratio and ISP rates for the selected tenants, period and platforms.",
    contract: { name: "ip-blocking-series", version: 1 },
    selection: {
      mode: "select",
      fields: ["effectiveness", "statusHours"],
      toParams: ({ fields = [] }) => ({ include_status_hours: fields.includes("statusHours") }),
    },
    report: {
      paramKeys: ["tenant_ids", "platforms", "excluded_isps", "date_basis", "country_source"],
      followScope: true,
    },
    buildParams: (params) => ({
      ...reportParams(params),
      ...platformParams(params),
      ...excludedIspsParams(params),
      date_basis: blockingDateBasis(params),
      country_source: ispCountrySource(params),
    }),
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<{
        ip_blocking_report: Pick<GqlIpBlockingCohorts, "freshness"> & {
          effectiveness: Pick<
            NonNullable<GqlIpBlockingCohorts["effectiveness"]>,
            "total" | "isps"
          > | null;
          status_hours?: IpBlockingSeriesData["statusHours"];
        };
      }>(query, params);
      const { status_hours: statusHours, ...report } = raw.ip_blocking_report;
      return {
        ...normalizeIpBlockingCohorts(
          {
            ...report,
            cohorts: null,
            safety_evidence: null,
            effectiveness: report.effectiveness
              ? {
                  ...report.effectiveness,
                  daily: [],
                  bottom_isps: [],
                }
              : null,
          },
          targetKind,
        ),
        statusHours: statusHours ?? null,
      } satisfies IpBlockingSeriesData;
    },
  };
}

export const BLOCKING_DATE_BASIS_OPTIONS = [
  { value: "creation", label: "Ticket creation" },
  { value: "blocking_window", label: "Active blocking window" },
];

/** Validate the blocking period filter, defaulting to ticket creation. */
function blockingDateBasis(params: Record<string, JsonValue>): string {
  const dateBasis = params.date_basis ?? "creation";
  if (!BLOCKING_DATE_BASIS_OPTIONS.some((option) => option.value === dateBasis))
    throw new Error("Choose a valid blocking date filter");
  return dateBasis as string;
}

export const ISP_COUNTRY_SOURCE_OPTIONS = [
  { value: "operator_name", label: "Operator name suffix" },
  { value: "operator_country", label: "Operator country column" },
];

/** Validate where ISP countries come from, defaulting to the operator name suffix. */
function ispCountrySource(params: Record<string, JsonValue>): string {
  const source = params.country_source ?? "operator_name";
  if (!ISP_COUNTRY_SOURCE_OPTIONS.some((option) => option.value === source))
    throw new Error("Choose a valid ISP country source");
  return source as string;
}

function safeBlockingSummarySource(targetKind: "IP" | "DOMAIN"): DataResolver {
  const args = `tenant_ids: $tenant_ids, from_ts: $from_ts, to_ts: $to_ts, date_basis: $date_basis, target_kind: ${JSON.stringify(targetKind)}`;
  const query = gql`query SafeBlockingSummary($tenant_ids: [Int!]!, $from_ts: String, $to_ts: String, $date_basis: String!) {
    ip_blocking_report(${args}, platform: "ALL") {
      cohorts { total { detected_unique_ips sent_unique_ips approved_unique_ips } observed_tenant_ids }
      safety_evidence { detected_unique_ips safe_unique_ips not_confirmed_safe_unique_ips safe_ratio safe_only_unique_ips unsafe_only_unique_ips conflicting_unique_ips unknown_unique_ips web_safe_ratio iptv_safe_ratio web_safe_unique_ips iptv_safe_unique_ips }
      freshness { generated_at data_through source is_estimated }
    }
  }`;
  return {
    label: targetKind === "IP" ? "Safe IP Summary" : "Safe Domain Summary",
    description:
      "Distinct detected or actively blocked targets with safety and sent ratios, independent of the page platform filter.",
    contract: { name: "ip-blocking-series", version: 1 },
    report: { paramKeys: ["tenant_ids", "date_basis"], followScope: true },
    buildParams: (params) => ({ ...reportParams(params), date_basis: blockingDateBasis(params) }),
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<{
        ip_blocking_report: Pick<GqlIpBlockingCohorts, "safety_evidence" | "freshness"> & {
          cohorts: Omit<NonNullable<GqlIpBlockingCohorts["cohorts"]>, "daily"> | null;
        };
      }>(query, params);
      const cohorts = raw.ip_blocking_report.cohorts;
      return {
        ...normalizeIpBlockingCohorts(
          { ...raw.ip_blocking_report, cohorts: cohorts && { ...cohorts, daily: [] } },
          targetKind,
        ),
        dateBasis: params.date_basis === "blocking_window" ? "blocking_window" : "creation",
        platformSafety: {
          web: raw.ip_blocking_report.safety_evidence?.web_safe_ratio ?? null,
          iptv: raw.ip_blocking_report.safety_evidence?.iptv_safe_ratio ?? null,
        },
        platformSafeCounts: {
          web: raw.ip_blocking_report.safety_evidence?.web_safe_unique_ips ?? null,
          iptv: raw.ip_blocking_report.safety_evidence?.iptv_safe_unique_ips ?? null,
        },
      } satisfies IpBlockingSeriesData;
    },
  };
}

const IP_BLOCKING_COMBINED_HOSTS_QUERY = gql`
  query IpBlockingCombinedHosts(
    $tenant_ids: [Int!]!
    $from_ts: String
    $to_ts: String
    $platform: String
    $platforms: [String!]
    $target_kind: String!
    $date_basis: String!
    $host_source: String!
  ) {
    ip_blocking_report(
      tenant_ids: $tenant_ids
      from_ts: $from_ts
      to_ts: $to_ts
      platform: $platform
      platforms: $platforms
      target_kind: $target_kind
      date_basis: $date_basis
      host_source: $host_source
    ) {
      combined_hosts {
        channel
        label
        unique_ips
        attributed_unique_ips
        unattributed_unique_ips
        hosts {
          name
          share
        }
      }
    }
  }
`;

function blockingHostsSource(targetKind: "IP" | "DOMAIN"): DataResolver {
  return {
    label: `${targetKind === "IP" ? "IP" : "Domain"} Blocking Top Hosts`,
    description:
      "Hosting providers of detected targets, from Armada endpoints or Hermes ticket IP owners.",
    contract: { name: "ranked-shares", version: 1 },
    // The widget's host source choice arrives as a selected field.
    selection: {
      mode: "select",
      fields: ["armada", "hermes"],
      toParams: ({ fields = [] }) => ({
        host_source: fields.includes("hermes") ? "hermes" : "armada",
      }),
    },
    report: { paramKeys: ["tenant_ids", "platform", "platforms", "date_basis"], followScope: true },
    buildParams: (params) => {
      const dateBasis = blockingDateBasis(params);
      const hostSource = params.host_source === "hermes" ? "hermes" : "armada";
      if (dateBasis === "blocking_window" && hostSource === "armada")
        throw new ScopeRequiredError(
          "Armada hosts have no blocking window: choose the Hermes host source or filter on ticket creation.",
        );
      return {
        ...ipBlockingParams(params),
        date_basis: dateBasis,
        host_source: hostSource,
      };
    },
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<{
        ip_blocking_report: { combined_hosts: GqlIpBlockingTopHosts["top_hosts"][number] | null };
      }>(IP_BLOCKING_COMBINED_HOSTS_QUERY, { ...params, target_kind: targetKind });
      const hosts = raw.ip_blocking_report.combined_hosts;
      return normalizeIpBlockingTopHosts({ top_hosts: hosts ? [hosts] : [] }, targetKind);
    },
  };
}

export const reportDataSources: Record<string, DataResolver> = {
  "blocking-overview-ip": blockingOverviewSource("IP"),
  "blocking-overview-domain": blockingOverviewSource("DOMAIN"),
  "safeip-summary": safeBlockingSummarySource("IP"),
  "safe-domain-summary": safeBlockingSummarySource("DOMAIN"),
  "ip-blocking-cohorts": ipBlockingCohortSource(false),
  "ip-blocking-effectiveness": ipBlockingCohortSource(true),
  "domain-blocking-cohorts": ipBlockingCohortSource(false, "DOMAIN"),
  "domain-blocking-effectiveness": ipBlockingCohortSource(true, "DOMAIN"),
  "ip-blocking-combined-hosts": blockingHostsSource("IP"),
  "domain-blocking-combined-hosts": blockingHostsSource("DOMAIN"),
  "piracy-audience-metrics": {
    label: "Piracy audience report",
    description: "Daily report for the configured tenant coverage.",
    contract: { name: "report-metrics", version: 1 },
    selection: metricSelection(AUDIENCE_METRICS),
    report: {
      paramKeys: ["tenant_ids", "countries", "hermes_sources", "platforms"],
      followScope: true,
    },
    buildParams: audienceMetricParams,
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<PiracyAudienceMetricsResponse>(
        PIRACY_AUDIENCE_METRICS_QUERY,
        params,
      );
      return normalizeReportMetrics(raw.piracy_audience_report);
    },
  },
  "piracy-audience": {
    label: "Piracy audience report",
    description: "Daily report for the configured tenant coverage.",
    contract: { name: "piracy-audience-series", version: 1 },
    selection: {
      mode: "select",
      fields: [
        "points",
        "blockingIntervals",
        "blockingPeriods",
        "blockingPeriods.id",
        "blockingPeriods.from",
        "blockingPeriods.to",
        "blockingPeriods.status",
        "blockingPeriods.viewersDisrupted",
        "blockingApplies",
        "reportMetrics",
        "freshness",
      ],
      toParams: ({ fields = [] }) => ({
        include_points: fields.includes("points"),
        include_intervals: fields.includes("blockingIntervals"),
        include_periods: fields.some(
          (field) => field === "blockingPeriods" || field.startsWith("blockingPeriods."),
        ),
        include_impact:
          fields.includes("blockingPeriods") || fields.includes("blockingPeriods.viewersDisrupted"),
        include_blocking_applies: fields.includes("blockingApplies"),
        include_freshness: fields.includes("freshness"),
      }),
    },
    report: {
      paramKeys: ["tenant_ids", "countries", "hermes_sources", "platforms"],
      followScope: true,
    },
    buildParams: audienceParams,
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<{ piracy_audience_report: GqlPiracyAudienceSeries }>(
        PIRACY_AUDIENCE_SERIES_QUERY,
        params,
      );
      return { ...normalizePiracyAudienceSeries(raw.piracy_audience_report), ...periodOf(params) };
    },
  },
  "ip-blocking-metrics": {
    label: "IP blocking report",
    description: "Daily report for the configured tenant coverage.",
    contract: { name: "report-metrics", version: 1 },
    selection: metricSelection(IP_METRICS),
    report: { paramKeys: ["tenant_ids", "platform", "platforms"], followScope: true },
    buildParams: (params) => ({
      ...ipBlockingParams(params),
      ...metricIdsParams(params, IP_METRICS),
    }),
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<{
        ip_blocking_report: { metrics: GqlReportMetric[]; freshness: GqlReportFreshness };
      }>(IP_BLOCKING_METRICS_QUERY, params);
      return normalizeReportMetrics(raw.ip_blocking_report);
    },
  },
  "ip-blocking": {
    label: "IP blocking report",
    description: "Observed daily IP counts; unavailable sources remain gaps.",
    contract: { name: "ip-blocking-series", version: 1 },
    report: { paramKeys: ["tenant_ids", "platform", "platforms"], followScope: true },
    buildParams: ipBlockingParams,
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<{ ip_blocking_report: GqlIpBlockingSeries }>(
        IP_BLOCKING_SERIES_QUERY,
        params,
      );
      return { ...normalizeIpBlockingSeries(raw.ip_blocking_report), ...periodOf(params) };
    },
  },
  "ip-blocking-top-hosts": {
    label: "IP blocking report - top hosts",
    description:
      "Hosts serving most detected IPs, per channel, for the configured tenant coverage.",
    contract: { name: "ranked-shares", version: 1 },
    report: { paramKeys: ["tenant_ids", "platform", "platforms"], followScope: true },
    buildParams: ipBlockingParams,
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<{ ip_blocking_report: GqlIpBlockingTopHosts }>(
        IP_BLOCKING_TOP_HOSTS_QUERY,
        params,
      );
      return normalizeIpBlockingTopHosts(raw.ip_blocking_report);
    },
  },
  "network-id": {
    label: "Network ID report",
    description: "Daily report for the configured tenant coverage.",
    contract: { name: "category-series", version: 1 },
    report: { paramKeys: ["tenant_ids"], followScope: true },
    buildParams: reportParams,
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<{ network_id_report: GqlNetworkIdReport }>(
        NETWORK_ID_QUERY,
        params,
      );
      return { ...normalizeNetworkIdReport(raw.network_id_report), ...periodOf(params) };
    },
  },
  "network-id-weekly": {
    label: "Network ID weekly",
    description: "Weekly network-group detections of one tenant over the dashboard period.",
    contract: { name: "network-id-weekly", version: 1 },
    report: { paramKeys: ["tenant_ids", "stream_ids"], followScope: true },
    buildParams: networkIdWeeklyParams,
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<{ network_id_weekly: GqlNetworkIdWeekly }>(
        NETWORK_ID_WEEKLY_QUERY,
        params,
      );
      return { ...normalizeNetworkIdWeekly(raw.network_id_weekly), ...periodOf(params) };
    },
  },
  "ott-piracy-metrics": {
    label: "OTT piracy report",
    description: "Daily report for the configured tenant coverage.",
    contract: { name: "report-metrics", version: 1 },
    report: { paramKeys: ["tenant_ids"], followScope: true },
    buildParams: reportParams,
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<{
        ott_piracy_report: { metrics: GqlReportMetric[]; freshness: GqlReportFreshness };
      }>(OTT_PIRACY_METRICS_QUERY, params);
      return normalizeReportMetrics(raw.ott_piracy_report);
    },
  },
  "ott-piracy-terms": {
    label: "OTT piracy report",
    description: "Daily report for the configured tenant coverage.",
    contract: { name: "weighted-terms", version: 1 },
    report: { paramKeys: ["tenant_ids"], followScope: true },
    buildParams: reportParams,
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<{
        ott_piracy_report: Parameters<typeof normalizeOttTerms>[0];
      }>(OTT_PIRACY_TERMS_QUERY, params);
      return normalizeOttTerms(raw.ott_piracy_report);
    },
  },
  "ott-piracy-chain": {
    label: "OTT piracy report",
    description: "Daily report for the configured tenant coverage.",
    contract: { name: "supply-chain", version: 1 },
    report: { paramKeys: ["tenant_ids"], followScope: true },
    buildParams: reportParams,
    fetcher: async (params, client: GraphQLClient) => {
      const raw = await client.request<{
        ott_piracy_report: Parameters<typeof normalizeOttChain>[0];
      }>(OTT_PIRACY_CHAIN_QUERY, params);
      return normalizeOttChain(raw.ott_piracy_report);
    },
  },
};
